import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Topo } from "@/components/Graphics";
import { Kicker, ScrollCTA } from "@/components/Bits";

export const SellMobileHome = () => (
  <section data-testid="sell-mobile-home" className="relative overflow-hidden border-y border-border bg-secondary/40">
    <div className="pointer-events-none absolute -right-[12%] top-[8%] w-[46vw] max-w-[560px] text-primary/15" aria-hidden="true">
      <Topo className="w-full h-full" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <Kicker>Sell Your Mobile Home With FaeVor Works</Kicker>
        <h2 className="font-display text-4xl sm:text-6xl tracking-mega leading-[0.9] mt-5 max-w-3xl">
          Your Next Move Starts With the Right Solution.
        </h2>
        <div className="mt-8 space-y-5 text-lg text-foreground/75 leading-relaxed max-w-2xl">
          <p>
            At FaeVor Works, we understand that selling a mobile or manufactured home isn't always as
            simple as selling a traditional house.
          </p>
          <p>
            Maybe your home is located in a mobile home park. Maybe it needs to be moved from its current
            property. Maybe it needs repairs, or you're simply ready for a fresh start.
          </p>
          <p>
            Whatever your situation, we're here to help you explore your options and move forward with
            confidence. We specialize in helping mobile and manufactured homeowners throughout{" "}
            <span className="text-foreground font-semibold">South Carolina</span> find practical
            solutions for selling their homes.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-14 rounded-3xl border border-border bg-background p-8 sm:p-10">
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
            We May Be Able to Help If Your Mobile Home:
          </h3>
          <ul className="mt-8 grid gap-x-10 gap-y-4 sm:grid-cols-2">
            {[
              "Is located in a mobile home park where you pay lot rent",
              "Is located on private property but needs to be moved",
              "Needs repairs or updates",
              "Has become difficult or expensive to maintain",
              "Was inherited and you no longer want to keep it",
              "Needs to be sold because you're relocating",
              "Is vacant or no longer being used",
              "Simply no longer fits your needs",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="text-primary mt-1 shrink-0" size={20} />
                <span className="text-base sm:text-lg text-foreground/80">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <p className="mt-12 text-lg text-foreground/75 leading-relaxed max-w-2xl">
          Our goal is simple: understand your situation, explain your options, and help create a selling
          solution that works for you.
        </p>
        <div className="mt-6 rounded-3xl bg-accent text-accent-foreground p-8 sm:p-10 max-w-3xl">
          <p className="text-xl sm:text-2xl font-display tracking-tight leading-snug">
            Depending on your home and circumstances, FaeVor Works may be able to provide a{" "}
            <span className="text-primary">cash purchase option</span> with a flexible closing timeline.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-16 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1] max-w-md">
              More Than a Sale — We're Here for the Transition.
            </h3>
            <div className="mt-6 space-y-5 text-lg text-foreground/75 leading-relaxed">
              <p>
                Selling your mobile home can represent the end of one chapter and the beginning of another.
              </p>
              <p>
                FaeVor Works provides professional communication and personalized service throughout the
                process. When additional services are needed, we can also help connect you with resources
                that may assist with the next steps.
              </p>
            </div>
          </div>
          <div className="rounded-3xl border border-border bg-background p-8 sm:p-10">
            <p className="text-lg text-foreground/75 leading-relaxed">
              When additional help is needed, we can connect you with trusted resources and professionals
              to support your next steps.
            </p>
            <p className="mt-6 font-display text-2xl tracking-tight">
              You don't have to figure everything out alone.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="mt-20 max-w-3xl">
          <h3 className="font-display text-3xl sm:text-5xl tracking-mega leading-[0.95]">
            You Have Options. Let's Find the Right One.
          </h3>
          <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
            Whether your mobile home stays where it is or needs to be relocated, start by telling us a
            little about your situation.
          </p>
          <p className="mt-3 text-lg text-foreground/75">No pressure. No obligation. Just options.</p>
          <p className="mt-6 text-sm uppercase tracking-[0.22em] text-primary font-semibold">
            Don't Miss Out on Your FaeVor!
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <ScrollCTA targetId="start" variant="solid" testid="cta-mobile-offer">
              Get My Mobile Home Offer
            </ScrollCTA>
            <span className="text-foreground/50 text-sm uppercase tracking-[0.2em]">or</span>
            <ScrollCTA targetId="start" variant="dark" testid="cta-selling-options">
              See My Selling Options
            </ScrollCTA>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const STEPS = [
  {
    num: "01",
    title: "Tell Us About Your Mobile Home",
    body: "Start by completing our short form. We'll ask for a few basic details about your home, such as:",
    list: [
      "Property location",
      "Year of the mobile home",
      "Bedrooms and bathrooms",
      "Approximate size",
      "Current condition",
      "In a mobile home park or on private property",
      "Whether the home will stay or needs to be moved",
      "Your desired selling timeline",
      "Photos, if available",
    ],
  },
  {
    num: "02",
    title: "We Review Your Property",
    body: "We'll review the information about your mobile home, including its location, condition, age, current setup, and potential selling options. If additional information is needed, we'll contact you.",
  },
  {
    num: "03",
    title: "Receive Your Selling Options",
    body: "Once we've evaluated your home, we'll discuss the potential solutions available for your situation. If your property meets our purchasing criteria, we may be able to present a cash purchase option. If another selling solution makes more sense, we'll explain those options so you can make an informed decision. There's no obligation to accept an offer.",
  },
  {
    num: "04",
    title: "We Help Coordinate the Process",
    body: "If you decide to move forward, FaeVor Works helps coordinate the transaction and keeps you informed along the way. Depending on your situation, this may involve working with buyers, title professionals, mobile home park management, movers, or other necessary parties. Our goal is to make the process organized, efficient, and as stress-free as possible.",
  },
  {
    num: "05",
    title: "Close & Move Forward",
    body: "Once the necessary requirements have been completed, ownership is transferred and the transaction is closed. Then you can focus on what's next.",
  },
];

const PRINCIPLES = [
  {
    num: "01",
    title: "Better opportunities start with better solutions.",
    body: "Our goal is to make real estate solutions easier to understand, easier to access, and far less overwhelming. Clarity is the first favor we offer.",
  },
  {
    num: "02",
    title: "We meet people exactly where they are.",
    body: "No two situations look the same. We listen first, help you understand your options, and lay out a clear path toward your next opportunity — without pressure.",
  },
  {
    num: "03",
    title: "A move with your property.",
    body: "Whether you're ready to sell your mobile home, or exploring options for a home that needs work, FaeVor Works is here to help you take the next step.",
  },
];

export const Principles = () => (
  <section data-testid="principles" className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
    <Reveal>
      <Kicker>What We Believe</Kicker>
    </Reveal>
    <div className="mt-10 border-t border-border">
      {PRINCIPLES.map((c, i) => (
        <Reveal key={c.num} delay={i * 0.05}>
          <div className="grid gap-6 md:grid-cols-[auto_1fr] md:gap-16 border-b border-border py-14">
            <span className="font-display text-6xl sm:text-8xl tracking-mega text-primary/80 leading-none">
              {c.num}
            </span>
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight leading-[0.98]">{c.title}</h2>
              <p className="mt-5 text-lg sm:text-xl text-foreground/70 leading-relaxed">{c.body}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const HowItWorks = () => (
  <section data-testid="how-it-works" className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
    <Reveal>
      <Kicker>How It Works</Kicker>
      <h2 className="font-display text-4xl sm:text-6xl tracking-mega leading-[0.9] mt-5 max-w-3xl">
        Selling your mobile home doesn't have to be complicated.
      </h2>
      <p className="mt-6 text-lg text-foreground/75 leading-relaxed max-w-2xl">
        At FaeVor Works, we've created a straightforward process designed to make selling your mobile or
        manufactured home as smooth and efficient as possible.
      </p>
    </Reveal>

    <div className="mt-16 border-t border-border">
      {STEPS.map((s, i) => (
        <Reveal key={s.num} delay={i * 0.04}>
          <div className="grid gap-5 md:grid-cols-[auto_1fr] md:gap-14 border-b border-border py-12">
            <span className="font-display text-5xl sm:text-7xl tracking-mega text-primary/80 leading-none">
              {s.num}
            </span>
            <div className="max-w-2xl">
              <h3 className="font-display text-2xl sm:text-4xl tracking-tight leading-[1]">{s.title}</h3>
              <p className="mt-4 text-lg text-foreground/70 leading-relaxed">{s.body}</p>
              {s.list && (
                <ul className="mt-6 grid gap-x-10 gap-y-3 sm:grid-cols-2">
                  {s.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check className="text-primary mt-1 shrink-0" size={18} />
                      <span className="text-base text-foreground/80">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal>
      <div className="mt-16 max-w-3xl">
        <h3 className="font-display text-3xl sm:text-5xl tracking-mega leading-[0.95]">
          Your Situation. Your Options. Your Next Move.
        </h3>
        <p className="mt-6 text-lg text-foreground/75">FaeVor Works is here to help make it easier.</p>
        <p className="mt-6 text-sm uppercase tracking-[0.22em] text-primary font-semibold">
          Don't Miss Out on Your FaeVor!
        </p>
        <div className="mt-8">
          <ScrollCTA targetId="start" variant="solid" testid="how-it-works-cta">
            See My Selling Options
          </ScrollCTA>
        </div>
      </div>
    </Reveal>
  </section>
);
